// src/app/reducers/userProfileReducer.ts

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import { Sphere } from 'types/global';
import { UserIdentity } from 'types/identities';
import { errorCodeOf } from '@center-pwa/utils/organizationMembership';

interface UserProfileState {
  userId: string | null;
  identityId: string | null;
  displayName: string | null;
  locale: string | null;
  userSphere: Sphere | null;
  identity: UserIdentity | null;
  fetchIdentityStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  fetchIdentityError: string | null;
  /** The `error` code the failed identity fetch answered with (e.g. `organization_not_in_world`), or null. */
  fetchIdentityErrorCode: string | null;
}

const initialState: UserProfileState = {
  userId: null,
  identityId: null,
  displayName: null,
  locale: 'en-US',
  userSphere: null,
  identity: null,
  fetchIdentityStatus: 'idle',
  fetchIdentityError: null,
  fetchIdentityErrorCode: null,
};

// Fetch user identity from the API
export const fetchIdentity = createAsyncThunk<
  UserIdentity,
  { identityId: string; apiClient: ApiClient },
  {
    rejectValue: string;
    rejectedMeta: { errorCode: string | null };
  }
>(
  'userProfile/fetchIdentity',
  async ({ identityId, apiClient }, thunkAPI) => {
    try {
      const response = await apiClient.get<{ identity: UserIdentity }>(
        `/identities/${identityId}/users`
      );
      const identityData = response.identity;

      // Set default user if only one exists
      if (!identityData.defaultUserId && identityData.users.length === 1) {
        identityData.defaultUserId = identityData.users[0].id;
      }

      return identityData;
    } catch (err: any) {
      console.error('Error fetching identity:', err);
      return thunkAPI.rejectWithValue(err.message ?? 'Unknown error', { errorCode: errorCodeOf(err) });
    }
  }
);

const userProfileSlice = createSlice({
  name: 'userProfile',
  initialState,
  reducers: {
    setUserId(state, action: PayloadAction<{ userId: string | null }>) {
      state.userId = action.payload.userId;
    },
    setUserSphere(state, action: PayloadAction<{ sphere: Sphere | null }>) {
      state.userSphere = action.payload.sphere;
    },
    setUserLocale(state, action: PayloadAction<{ locale: string | null }>) {
      state.locale = action.payload.locale;
    },
    setTokenClaims(
      state,
      action: PayloadAction<{ identityId: string; displayName: string }>
    ) {
      state.identityId = action.payload.identityId;
      state.displayName = action.payload.displayName;
    },
    setFavoriteUser(state, action: PayloadAction<{ userId: string | null }>) {
      if (state.identity) {
        state.identity.defaultUserId = action.payload.userId ?? undefined;
      }
    },
    clearUserProfile() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIdentity.pending, (state) => {
        state.fetchIdentityStatus = 'loading';
        state.fetchIdentityError = null;
        state.fetchIdentityErrorCode = null;
      })
      .addCase(
        fetchIdentity.fulfilled,
        (state, action: PayloadAction<UserIdentity>) => {
          state.fetchIdentityStatus = 'succeeded';
          state.identity = action.payload;
        }
      )
      .addCase(fetchIdentity.rejected, (state, action) => {
        state.fetchIdentityStatus = 'failed';
        state.fetchIdentityError = action.payload ?? action.error.message ?? null;
        state.fetchIdentityErrorCode = action.meta.rejectedWithValue ? action.meta.errorCode : null;
      });
  },
});

export const {
  setUserId,
  setTokenClaims,
  setUserLocale,
  setUserSphere,
  setFavoriteUser,
  clearUserProfile,
} = userProfileSlice.actions;

export default userProfileSlice.reducer;
